/** Edits a fact (spec §7.2): its sentence, its status and the qualifiers it keeps. Saving is Part 05's fact edit; a sentence
 * another active fact already holds there comes back as 409 fact_exists. Esc cancels. */
import { useState } from "react";
import {
  isFactExistsError,
  type EpistemicStatus,
  type MemoryFact,
} from "@/api/memory-focus";
import {
  Button,
  ChipGroup,
  Icons,
  PocketRow,
  PocketSection,
  TextInput,
} from "@/components/ui";
import { commonMessages } from "@/i18n/messages/common";
import { useT } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import type en from "@/i18n/messages/frames-memory/en";
import { qualifierLabel } from "@/lib/memory-thing-kinds";
import { useMemoryStore } from "@/stores/memory-store";
const STATUS_KEY = {
  fact: "fact",
  goal: "statusGoal",
  decision: "statusDecision",
  hypothesis: "statusHypothesis",
} as const satisfies Record<EpistemicStatus, keyof typeof en>;
const STATUSES = Object.keys(STATUS_KEY) as EpistemicStatus[];
export function PocketFactEditor({
  fact,
  onDone,
}: {
  fact: MemoryFact;
  onDone: () => void;
}) {
  const t = useT(framesMemoryMessages);
  const common = useT(commonMessages);
  const [claim, setClaim] = useState(fact.claim);
  const [status, setStatus] = useState<EpistemicStatus>(fact.epistemic_status);
  const [qualifiers, setQualifiers] = useState(fact.qualifiers);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed =
    claim.trim() !== fact.claim ||
    status !== fact.epistemic_status ||
    qualifiers.length !== fact.qualifiers.length;
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await useMemoryStore.getState().saveFact(fact.id, {
        claim: claim.trim(),
        epistemic_status: status,
        qualifiers: qualifiers.map((qualifier) => ({
          kind: qualifier.kind,
          target_id: qualifier.target.id,
        })),
      });
      onDone();
    } catch (failure) {
      setError(isFactExistsError(failure) ? t("factExists") : t("saveFailed"));
      setSaving(false);
    }
  };
  return (
    <div
      data-testid="memory-fact-editor"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onDone();
        }
      }}
    >
      <div className="px-2">
        <TextInput
          value={claim}
          onChange={(event) => setClaim(event.target.value)}
          aria-label={t("fact")}
          data-testid="memory-fact-claim"
        />
        <ChipGroup
          className="mt-2 grid-flow-row grid-cols-2"
          variant="segmented"
          ariaLabel={t("kindOfStatement")}
          value={status}
          onChange={setStatus}
          options={STATUSES.map((value) => ({
            value,
            label: t(STATUS_KEY[value]),
            testId: `memory-fact-status-${value}`,
          }))}
        />
      </div>
      {qualifiers.length > 0 ? (
        <PocketSection title={t("applies")}>
          {qualifiers.map((qualifier) => (
            <PocketRow
              key={`${qualifier.kind}:${qualifier.target.id}`}
              testId={`memory-fact-qualifier-${qualifier.target.id}`}
              title={qualifierLabel(qualifier)}
              trailing={
                <Button
                  variant="ghost"
                  size="iconSm"
                  aria-label={t("removeQualifier", {
                    qualifier: qualifierLabel(qualifier),
                  })}
                  data-testid={`memory-fact-remove-${qualifier.target.id}`}
                  onClick={() =>
                    setQualifiers((current) =>
                      current.filter((item) => item !== qualifier),
                    )
                  }
                >
                  <Icons.X className="h-4 w-4" aria-hidden="true" />
                </Button>
              }
            />
          ))}
        </PocketSection>
      ) : null}
      {error ? (
        <p
          className="px-2 pt-2 text-appkit-caption text-danger"
          role="alert"
          data-testid="memory-fact-error"
        >
          {error}
        </p>
      ) : null}
      <div className="mt-3 flex gap-2 px-2">
        <Button
          size="sm"
          disabled={!changed || !claim.trim() || saving}
          onClick={() => void save()}
          data-testid="memory-fact-save"
        >
          {common("save")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onDone}
          data-testid="memory-fact-cancel"
        >
          {common("cancel")}
        </Button>
      </div>
    </div>
  );
}
